/**
 * Action Plan - Client-Side Type Definitions
 *
 * TypeScript interfaces for Strategic Action Plan feature (frontend).
 * Mirrors backend types from server/types/actionPlan.ts
 *
 * @see server/types/actionPlan.ts - Server-side source of truth
 * @see Documentation/Development/Design Docs/Strategic_Action_Plan_Design_And_Implementation_Plan_v2.0.md
 */

import type {
  DerivedFinancials,
  FairScoresQuantified,
  PlanWarning,
  Quantified,
} from './actionPlanValueProvenance';
import type {
  AcknowledgedAssumption,
  AssumptionSummaryEntry,
} from './actionPlanAssumptions';
export type { AcknowledgedAssumption, AssumptionSummaryEntry };

export type { DerivedFinancials, FairScoresQuantified, PlanWarning, Quantified };

// ============================================================================
// CORE TYPES
// ============================================================================

/**
 * What a client may SET. Deliberately narrower than what may be READ.
 */
export type TaskStatus = 'not_started' | 'in_progress' | 'completed';
/** What may be STORED or READ — a superset of the settable vocabulary. */
export type TaskStatusPersisted = TaskStatus | 'blocked' | 'skipped';

export type TaskPriority = 'critical' | 'high' | 'medium' | 'low';
export type TaskImpact = 'high' | 'medium' | 'low';
export type DependencyType = 'hard' | 'soft';
export type DependencySource = 'manual' | 'heuristic' | 'ai_suggested';
export type ValueRealizationType = 'immediate' | 'staged' | 'cumulative';
/**
 * The six PEER Action Plan views (S5 production).
 *
 * `dependency` and `valuecurve` were previously side toggles rather than views, so
 * moving to them read as leaving the plan and they could not hold selection. They are
 * peers now, and `matrix` is the unconditional default at every plan size — large-plan
 * usability comes from search, filters, counts, stable ordering and density, not from
 * switching the landing view.
 *
 * ORDER IS THE TAB ORDER. Matrix first because it is visually primary.
 */
export const ACTION_PLAN_VIEWS = [
  'matrix',
  'list',
  'timeline',
  'prism',
  'dependency',
  'valuecurve',
] as const;

export type ViewMode = (typeof ACTION_PLAN_VIEWS)[number];

/** The default view. Matrix, unconditionally. */
export const DEFAULT_ACTION_PLAN_VIEW: ViewMode = 'matrix';

/** Human labels. No emoji — retired in v1.2 and not reintroduced here. */
export const ACTION_PLAN_VIEW_LABELS: Record<ViewMode, string> = {
  matrix: 'Matrix',
  list: 'List',
  timeline: 'Timeline',
  prism: 'Prism',
  dependency: 'Dependency',
  valuecurve: 'Value Curve',
};

/** Narrow an untrusted string (a URL parameter) to a view. */
export function asViewMode(value: string | null | undefined): ViewMode | null {
  return value && (ACTION_PLAN_VIEWS as readonly string[]).includes(value)
    ? (value as ViewMode)
    : null;
}

// ============================================================================
// FAIR SCORES
// ============================================================================

export interface FairScores {
  F: number; // Findable (0-5)
  A: number; // Accessible (0-5)
  I: number; // Interoperable (0-5)
  R: number; // Reusable (0-5)
}

// ============================================================================
// TASK
// ============================================================================

export interface Task {
  id: string;
  recommendation_id: string;
  station_name: string;

  title: string;
  description: string;
  implementation_steps: string[];

  priority: TaskPriority;
  impact: TaskImpact;
  /**
   * Estimated effort in person-weeks, or `null` when no usable estimate is stored.
   *
   * NOT a zero. `action_plan_tasks.estimated_hours` is nullable, and the read path used to
   * report a missing estimate as `0` weeks — which the Matrix plotted in the low-effort
   * column and the Timeline drew as a zero-duration bar. Mirrors the server exactly; see
   * `server/services/effortNormalization.ts` for the normalization rules.
   *
   * `number | null` rather than `Quantified<number>`: there is an absence to represent but
   * no authoritative effort-provenance contract to fill in.
   *
   * A genuine `0` is AVAILABLE and renders as `0w`.
   */
  effort_weeks: number | null;

  phase_id: string;
  start_week: number;
  end_week: number;

  fair_rules: string[];
  business_value_drivers: string[];
  /**
   * Total business value (USD), with provenance.
   *
   * An unquantified task is NOT a zero-value task (S4d). The Matrix places
   * unavailable tasks in an explicit region rather than at the origin.
   */
  value_usd: Quantified<number>;
  value_calculation?: ValueCalculationDetails;

  dependencies: string[]; // Array of task IDs
  is_critical_path: boolean;

  /**
   * PERSISTED status, not input status. `blocked` and `skipped` are real
   * storable states (migration 021:137, retained by 141) that the read model
   * must round-trip honestly — typing this as the three-state input vocabulary
   * forced unsafe casts and made the UI render both as "not started", which is
   * a fabricated state exactly like the ones this remediation removes.
   * Mutation input stays three-state; see UpdateTaskStateResponse.
   */
  status: TaskStatusPersisted;
  completed_at?: string;
  completed_by?: string;

  /**
   * Optimistic token for this task, echoed back on every status change and sent
   * as `expected_lock_version` on the next one. Optional because plans loaded
   * before migration 141 have none; the client sends 1 in that case and the
   * server's own guard decides.
   */
  lock_version?: number;
  /** See TaskStateProvenance — `legacy_unverified` is a migrated default, not an observed state. */
  state_provenance?: TaskStateProvenance;

  value_realization?: ValueRealizationRule;

  // Sprint 9: Source level for provenance tracking (DC-446)
  source_level?: 'journey' | 'organization' | 'merged';
}

// ============================================================================
// VALUE CALCULATION (Enhanced for Pistoia Integration)
// ============================================================================

export type ConfidenceLevel = 'High' | 'Medium' | 'Low';

export interface ValueCalculationDetails {
  base_value_usd: number;
  fair_improvements: FairImprovement[];
  multipliers: {
    priority: number;
    industry: number;
  };
  total_value_usd: number;
  npv_3yr_usd: number; // Net Present Value over 3 years
  formula_trace: string; // Human-readable calculation chain

  // NEW: Pistoia Integration Fields
  pistoia_driver?: PistoiaDriverInfo;
  evidence_chain?: EvidenceChain;
  validation_result?: ValidationResult;
  confidence_info?: ConfidenceInfo;
  attribution_info?: AttributionInfo;
  station_sources?: StationSource[];
}

export interface FairImprovement {
  rule: string; // e.g., "F2"
  driver: string; // e.g., "Data Discoverability"
  current_state: string; // e.g., "60 datasets, 15% discoverable"
  improved_state: string; // e.g., "60 datasets, 45% discoverable"
  calculation: string; // e.g., "18 datasets × $8K = $144K/year"
  annual_value_usd: number;
}

// ============================================================================
// PISTOIA DRIVER INFO
// ============================================================================

export interface PistoiaDriverInfo {
  driver_id: string; // e.g., "cb1-7D3A4B9E"
  driver_name: string; // e.g., "Faster Decisions"
  driver_category: string; // e.g., "Time Savings"
  formula: string; // e.g., "(time_baseline - time_FAIR) × requests × cost/hr"
  parameters: Record<string, number | string>; // Actual values used
}

// ============================================================================
// EVIDENCE CHAIN (Traceability)
// ============================================================================

export interface EvidenceChain {
  bridge_recommendation_id: string;
  parameter_sources: ParameterSource[];
  calculation_steps: CalculationStep[];
}

export interface ParameterSource {
  parameter_name: string; // e.g., "time_baseline"
  value: number | string;
  source_type: 'bridge_recommendation' | 'station_data' | 'organization_context' | 'default';
  source_description: string; // e.g., "Bridge AI: 'researchers spend 5 hours per search'"
  source_location?: string; // e.g., "impact_assessment.current_state_description"
}

export interface CalculationStep {
  step_number: number;
  description: string; // e.g., "Calculate time saved per search"
  formula: string; // e.g., "5.0 - 1.2 = 3.8 hours"
  result: number;
  unit?: string;
}

// ============================================================================
// VALIDATION RESULT
// ============================================================================

export interface ValidationResult {
  is_valid: boolean;
  warnings: ValidationWarning[];
  passed_rules: string[];
  failed_rules: string[];
}

export interface ValidationWarning {
  rule: string; // e.g., "time_direction"
  severity: 'error' | 'warning' | 'info';
  message: string; // e.g., "Target time (1.2h) should be less than baseline (5.0h)"
  suggestion?: string; // e.g., "Check Bridge recommendation for correct time values"
}

// ============================================================================
// CONFIDENCE INFO
// ============================================================================

export interface ConfidenceInfo {
  level: ConfidenceLevel;
  multiplier: number; // 1.0 (High), 0.8 (Medium), 0.6 (Low)
  explanation: string; // e.g., "Medium confidence: Bridge priority=high but stakeholder readiness=mixed"
  factors: ConfidenceFactor[];
}

export interface ConfidenceFactor {
  factor_name: string; // e.g., "Priority"
  value: string; // e.g., "high"
  impact: 'positive' | 'neutral' | 'negative';
  source: string; // e.g., "Bridge recommendation"
}

// ============================================================================
// ATTRIBUTION INFO
// ============================================================================

export interface AttributionInfo {
  percentage: number; // 55-85%
  explanation: string; // e.g., "70% attribution: Medium complexity implementation"
  complexity_breakdown: ComplexityBreakdown[];
}

export interface ComplexityBreakdown {
  step: string;
  complexity: 'low' | 'medium' | 'high';
  rationale?: string;
}

// ============================================================================
// STATION SOURCES
// ============================================================================

export interface StationSource {
  station_id: string;
  station_name: string;
  station_icon: string; // e.g., "📊", "🧬", "💬"
  contribution: string; // e.g., "Quality score: 66/100"
  data_point?: string; // Specific metric extracted
}

// ============================================================================
// CONFIDENCE DISTRIBUTION (for header summary)
// ============================================================================

export interface ConfidenceDistribution {
  high: { count: number; value_usd: number };
  medium: { count: number; value_usd: number };
  low: { count: number; value_usd: number };
}

// ============================================================================
// VALUE REALIZATION
// ============================================================================

export interface ValueRealizationRule {
  task_id: string;
  realization_type: ValueRealizationType;
  value_start_trigger: string; // e.g., "task_completion" | "task-004.completed" | "all(['task-003','task-004']).completed"
  ramp_up_weeks: number; // 0-12, time to reach full value
  dependencies: string[]; // Tasks that must complete before value starts
  explanation: string; // Why this realization model was chosen
}

export interface ValueRealizationSchedule {
  timeline: Array<{
    week: number;
    value_realized_usd: number;
    cumulative_value_usd: number;
    tasks_completed: string[];
    milestones: string[];
  }>;
}

// ============================================================================
// DEPENDENCY
// ============================================================================

export interface Dependency {
  from_task_id: string; // Prerequisite task
  to_task_id: string; // Dependent task
  type: DependencyType;
  reason?: string; // Explanation for dependency
  source: DependencySource; // How was this dependency detected?
  confidence?: number; // 0-1, for AI-suggested dependencies
}

// ============================================================================
// PHASE
// ============================================================================

export interface Phase {
  id: string;
  name: string; // "Foundation" | "Standardization" | "Automation"
  description: string;
  order: number; // 1, 2, 3
  start_week: number;
  end_week: number;
  task_ids: string[];
  total_value_usd: number;
  /**
   * Percent of this phase's tasks completed, or `null` when it has no resolvable tasks.
   *
   * OVERLAID from the rendered, state-overlaid tasks assigned to the phase — never the stored
   * snapshot. `phases` used to pass through `raw_json` untouched, so this held whatever was
   * written at GENERATION time, before any task state existed; a phase could report 100% while
   * every task in it was `not_started`. `null` rather than 0, because 0 claims that none of the
   * phase's work is done, and there is no work to speak of.
   */
  progress_pct: number | null;
}

// ============================================================================
// ACTION PLAN SUMMARY
// ============================================================================

export interface ActionPlanSummary {
  // Task counts
  tasks_not_started: number;
  tasks_in_progress: number;
  tasks_completed: number;
  /**
   * Percent of tasks completed, or `null` when the plan has no tasks.
   *
   * NOT 0 for an empty plan. The read paths wrote `total > 0 ? ... : 0`, reporting a plan with
   * nothing in it as 0% complete — a measurement of something absent. Consumers must render
   * `null` as unavailable.
   */
  overall_progress_pct: number | null;

  /**
   * Total projected business value (USD), with provenance.
   *
   * AGGREGATION RULE: unavailable whenever any rendered task lacks a value. A
   * partial sum is a priced subtotal and must never be labelled Value, Total
   * Value, or an ROI input.
   */
  total_value_usd: Quantified<number>;
  /** Renamed from value_realized_usd — it was hardcoded to 0 (P1-7) */
  value_forecast_usd: number | null;
  value_forecast_pct: number | null; // 0-100

  // Timeline
  weeks_remaining: number | null;
  critical_path_length: number;
  next_milestone?: string;
}

// ============================================================================
// ACTION PLAN (MAIN DATA STRUCTURE)
// ============================================================================

export interface ActionPlan {
  journey_id: string;
  generated_at: string; // ISO 8601 timestamp
  last_updated: string; // ISO 8601 timestamp
  version: string; // "2.0"
  schema_version: string; // "2.0.0" - for migration tracking
  algorithm_version: string; // "1.2.3" - for rollback detection

  summary: ActionPlanSummary;
  phases: Phase[];
  tasks: Task[];
  dependencies: Dependency[];
  value_realization: ValueRealizationSchedule;

  /** ROI and payback, computed server-side so provenance travels (P0-4) */
  derived: DerivedFinancials;

  /** Plan-level warnings, rendered rather than console.warn'd (P1-8) */
  warnings: PlanWarning[];

  metadata: {
    total_recommendations: number;
    total_tasks: number;
    total_value_usd: Quantified<number>;
    /** Renamed from total_value_realized_usd — nothing measures realization (P1-7) */
    total_value_forecast_usd: Quantified<number>;
    fair_score_current: FairScoresQuantified;
    fair_score_projected: FairScoresQuantified;
    estimated_duration_weeks: Quantified<number>;
  };
}

// ============================================================================
// API REQUEST/RESPONSE TYPES
// ============================================================================

export interface GenerateActionPlanRequest {
  options?: {
    phase_duration_weeks?: number;
    start_date?: string; // ISO 8601 date
    include_business_value?: boolean;
    effort_estimation_mode?: 'conservative' | 'moderate' | 'aggressive';
    /**
     * `manual` REMOVED (S4d follow-up). The server accepts only these two, so a
     * client sending `manual` received a 400 that neither type predicted.
     */
    dependency_mode?: 'heuristic' | 'ai_assisted';
  };

  /**
   * Explicitly acknowledged assumptions (S4d).
   *
   * This was missing from the client type entirely, so the panel built its
   * request as an anonymous object literal and nothing checked that the field it
   * sent was the field the server validates. The panel now types the request with
   * this interface.
   *
   * OMITTED, not empty, when nothing was acknowledged — "Continue without these
   * inputs". `organization_context` is deliberately absent: the server rejects it.
   */
  acknowledged_assumptions?: AcknowledgedAssumption[];
}

/** Mirrors the server's `DependencySuggestion`. Narrower than `Dependency`. */
export interface DependencySuggestion {
  from_task_id: string;
  to_task_id: string;
  reason: string;
  confidence: number;
  source: 'manual' | 'heuristic' | 'ai_suggested';
}

export interface GenerateActionPlanResponse {
  action_plan: ActionPlan;
  /**
   * REQUIRED, and its own shape (S4d follow-up).
   *
   * Two defects in one line: it was optional while the server always sends it, and
   * it was typed `Dependency[]` while the server sends a narrower suggestion shape
   * with no `id` or `type`. A consumer reading `suggestion.id` would have found
   * undefined, and nothing compared the envelopes so nothing flagged either.
   */
  dependency_suggestions: DependencySuggestion[];
  /**
   * Structured, not `string[]?` (P1-8, S4d follow-up).
   *
   * The client declared `warnings?: string[]` while the server returned
   * `PlanWarning[]`, and nothing compared them — the same shape of defect as
   * P0-3. Rendering a `PlanWarning` object as a string would have produced
   * "[object Object]" in the UI.
   */
  warnings: PlanWarning[];

  /** What the plan was priced on — same field after generation and regeneration (S4d). */
  assumption_summary: AssumptionSummaryEntry[];
}

// UpdateTaskStatusRequest was REMOVED in S3. The request is described once, by
// the server's Zod schema; the client sends { status, expected_lock_version }.
// `completed_by` was REMOVED from the REQUEST (P0-1) — the server resolves the
// actor from the verified JWT and rejects a body that names one. `Task.completed_by`
// remains as a READ field: it is what the server recorded, not what a client asked for.

/**
 * Task state the SERVER owns. Mirrors server/services/actionPlanTaskState.ts;
 * equality is enforced by client/types/__tests__/actionPlanTaskState.parity.test.ts.
 */
/**
 * `legacy_unverified` = the row predates migration 141 and has not been
 * transitioned since, so its status is the migrated default rather than an
 * observed fact. The UI must not present it as a choice someone made.
 */
export type TaskStateProvenance = 'canonical' | 'legacy_unverified';

export interface CanonicalTask {
  task_id: string;
  status: TaskStatusPersisted;
  completed_at: string | null;
  completed_by: string | null;
  lock_version: number;
  state_provenance: TaskStateProvenance;
}

/** Counts only. No financials — see the server-side note on why. */
export interface TaskProgress {
  tasks_not_started: number;
  tasks_in_progress: number;
  tasks_completed: number;
  tasks_blocked: number;
  tasks_skipped: number;
  total_tasks: number;
  overall_progress_pct: number;
}

/**
 * Replaces the old UpdateTaskStatusResponse, which declared `task: Task` and
 * `action_plan_summary: ActionPlanSummary` — the first was never sent (P0-3:
 * the panel wrote `undefined` into state) and the second coupled a status
 * change to financial forecasts.
 *
 * `task` is a STATE DELTA. Merge it into the existing task; replacing the task
 * with this object would discard title, description, dependencies, and value.
 * `progress` merges into the displayed summary, leaving unrelated financial
 * fields untouched.
 */
export interface UpdateTaskStateResponse {
  task: CanonicalTask;
  progress: TaskProgress;
  affected_tasks: string[];
}

// ============================================================================
// UI STATE TYPES
// ============================================================================

export interface ActionPlanViewState {
  view_mode: ViewMode;
  selected_task_id: string | null;
  selected_phase_id: string | null;
  show_dependencies: boolean;
  show_value_details: boolean;
  filter_status: TaskStatus | 'all';
  filter_priority: TaskPriority | 'all';
  sort_by: 'priority' | 'value' | 'start_week' | 'effort';
  sort_direction: 'asc' | 'desc';
}

/*
 * `MatrixQuadrant` was REMOVED in S4f-A. The Matrix renders the approved 3×2
 * display geometry (`MatrixValueRow` × `MatrixEffortColumn` with
 * `MATRIX_CELL_LABELS` in `client/components/actionPlanMatrixPlacement.ts`);
 * the comparative placement contract lives on `placeOnGrid`'s
 * `ComparativeCellId`, unchanged. The old 2×2 quadrant view-model had no
 * remaining consumers.
 */
